/**
 * Where an actor is, and the two answers to "is it here" (D232;
 * `docs/design/actors.md` §2, `actors-content.md` §5.2).
 *
 * @remarks
 * One module because the three reads below are one rule from three ends,
 * and every consumer — the catalog, the `ask` handler, the item verbs'
 * refusal, the two conditions — must agree about where a body is:
 *
 * - **Where it stands.** `world.actorLocations` holds only the actors that
 *   have MOVED from where the pack put them (the "no derived data" rule, as
 *   `droppedItemLocations` is for things); an actor absent from it stands at
 *   its `initialRoomId`, or nowhere when it declares none. D233's
 *   `move_actor` (`actor-movement.ts`) is the one writer, and it writes the
 *   delta: a body moved back to where the pack put it leaves the record.
 *   `actor_in_room` reads this alone — where a body stands is a fact about
 *   the world, whoever is looking.
 * - **Present** — the body is in the player's room and visible: within
 *   reach. What the item verbs' refusals read before they refuse in the
 *   actor's own words, and what round C's `blocksExits` and round D's
 *   `attack` will read.
 * - **Addressable** — the actor can be SPOKEN TO from here: present, or
 *   heard from this room through its `audibleFrom`. What `ask` and `talk`
 *   read, and what puts an actor in the catalog at all. D235: earshot does
 *   not need the body on stage — a voice with a location that is not the
 *   player's (the occupant behind the steel door, a voice at the far end of
 *   a handset) declares no `initialRoomId` and the rooms it is heard from;
 *   it is never present anywhere and addressable from those. Only a body a
 *   `move_actor` has sent OFFSTAGE is silenced: gone is gone.
 *
 * Calder's occupant behind the steel door is the case that split the two:
 * she is addressable and not reachable — `ask her about the film` works,
 * `attack her` refuses with the door's reason, never with "she is not here".
 * An actor's `visibleWhen` gates both answers, exactly as an item's
 * visibility condition does: unmet, the actor is not referable at all, so
 * nothing can leak its existence through a which-one question.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorId, RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';

/**
 * The room an actor stands in now, or `undefined` when it is offstage —
 * declared and nowhere.
 *
 * @param bundle - The loaded bundle (the declared starting rooms)
 * @param state - The live runtime state (the moves since)
 * @param actorId - The actor
 * @returns Its room, or `undefined` offstage
 */
export const roomOfActor = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): RoomId | undefined => {
	const moved = state.world.actorLocations[actorId];

	if (moved !== undefined) {
		return moved === 'offstage' ? undefined : moved;
	}

	return requireActor(bundle, actorId).initialRoomId;
};

/**
 * Does the actor STAND in the named room — a fact about the world, read from
 * anywhere, blind to the player's position and to `visibleWhen` (D233's
 * `actor_in_room`)?
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actorId - The actor
 * @param roomId - The room asked about
 * @returns Whether the actor stands there now
 */
export const isActorInRoom = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId, roomId: RoomId): boolean =>
	roomOfActor(bundle, state, actorId) === roomId;

/**
 * Whether an actor may be referred to at all right now — its `visibleWhen`
 * holds, or it declares none.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actor - The actor
 * @returns Whether the actor is visible
 */
export const isActorVisible = (bundle: EngineGameBundle, state: RuntimeState, actor: ActorDefinition): boolean =>
	actor.visibleWhen === undefined || evaluateCondition(actor.visibleWhen, bundle, state);

/**
 * Is the actor PRESENT — in the player's room, and visible — the body within
 * reach?
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actorId - The actor
 * @returns Whether the actor is here to be reached
 */
export const isActorPresent = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): boolean =>
	roomOfActor(bundle, state, actorId) === state.progression.activeRoomId && isActorVisible(bundle, state, requireActor(bundle, actorId));

/**
 * Is the actor ADDRESSABLE — present, or heard from the player's room
 * through its `audibleFrom` — a voice within earshot?
 *
 * @remarks
 * A body a `move_actor` has sent offstage is not heard from anywhere: gone
 * is gone. A voice that was never placed (no `initialRoomId`, never moved)
 * is heard from its `audibleFrom` rooms all the same — D235's
 * offstage-with-earshot reading, the occupant's.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actorId - The actor
 * @returns Whether the actor is here to be spoken to
 */
export const isActorAddressable = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): boolean => {
	const actor = requireActor(bundle, actorId);

	if (state.world.actorLocations[actorId] === 'offstage' || !isActorVisible(bundle, state, actor)) {
		return false;
	}

	return roomOfActor(bundle, state, actorId) === state.progression.activeRoomId || actor.audibleFrom.includes(state.progression.activeRoomId);
};

/**
 * The actors the player can address from where they stand, in the pack's
 * declared order — the catalog's actor walk, and the `ask` handler's
 * "whoever here knows" order for actors.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The addressable actors, in declared order
 */
export const actorsAddressableHere = (bundle: EngineGameBundle, state: RuntimeState): readonly ActorDefinition[] =>
	bundle.contentPack.actors.filter((actor) => isActorAddressable(bundle, state, actor.actorId));

/**
 * Can this body be GONE THROUGH (D240) — does it carry anything (a thing
 * whose `containedIn.containerActorId` names it), or answer a search in its
 * own words (`searchNarratives`)?
 *
 * @remarks
 * Read by the actor intercept, which narrows `search <actor>` only for such
 * a body (any other refuses as every item verb on a body does), and by the
 * room survey, which offers such a body as worth going through when it is
 * within reach.
 *
 * @param bundle - The loaded bundle
 * @param actor - The body
 * @returns Whether `search` has something to do on it
 */
export const isActorSearchable = (bundle: EngineGameBundle, actor: ActorDefinition): boolean =>
	actor.searchNarratives !== undefined || (bundle.indexes.carriedItemIdsByActorId.get(actor.actorId)?.length ?? 0) > 0;
